import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SEOHelmet = ({
  title,
  description,
  keywords,
  url,
  type = "website",
  image = "https://webcenter.com.tr/assets/images/logo.webp",
  author = "Web Center"
}) => {
  const location = useLocation();

  const baseUrl = "https://webcenter.com.tr";

  const defaultTitle =
    "Web Center | Bursa Web Tasarım ve Dijital Çözümler";

  const defaultDescription =
    "Web Center; Bursa merkezli profesyonel web tasarım, kurumsal web sitesi, e-ticaret sitesi, SEO uyumlu web tasarım ve dijital çözümler sunar.";

  const defaultKeywords =
    "web tasarım, Bursa web tasarım, Nilüfer web tasarım, kurumsal web tasarım, web tasarım firması, e-ticaret sitesi, SEO uyumlu web tasarım, mobil uyumlu web sitesi, Web Center";

  // =====================================================
  // SAYFA BAZLI SEO
  // =====================================================

  const getPageSEO = () => {
    const path = location.pathname.toLowerCase();

    const seoData = {
      "/": {
        title: defaultTitle,
        description: defaultDescription,
        keywords: defaultKeywords,
        schemaType: "WebPage"
      },

      "/web-design": {
        title: "Profesyonel Web Tasarım | Web Center Bursa",
        description:
          "Modern, mobil uyumlu, hızlı ve SEO odaklı profesyonel web sitesi tasarımı. Web Center ile işletmenize özel web çözümleri.",
        keywords:
          "web tasarım, Bursa web tasarım, Nilüfer web tasarım, profesyonel web tasarım, kurumsal web sitesi, responsive web tasarım",
        schemaType: "Service",
        serviceName: "Profesyonel Web Tasarım",
        serviceType: "Web Tasarım ve Web Sitesi Geliştirme"
      },

      "/e-ticaret": {
        title: "E-Ticaret Sitesi Tasarımı | Web Center Bursa",
        description:
          "Mobil uyumlu, kullanıcı dostu ve SEO odaklı profesyonel e-ticaret sitesi çözümleri ile online satış altyapınızı oluşturun.",
        keywords:
          "e-ticaret sitesi, Bursa e-ticaret, e-ticaret web tasarım, online mağaza, e-ticaret çözümleri",
        schemaType: "Service",
        serviceName: "E-Ticaret Sitesi Tasarımı",
        serviceType: "E-Ticaret Sitesi Geliştirme"
      },

      "/domain-hosting": {
        title: "Domain ve Hosting Hizmetleri | Web Center",
        description:
          "Web projeleriniz için domain ve hosting çözümleri. Web sitenizin yayın altyapısı için Web Center hizmetlerini inceleyin.",
        keywords:
          "domain hosting, web hosting, domain kayıt, Bursa hosting, hosting hizmetleri",
        schemaType: "Service",
        serviceName: "Domain ve Hosting",
        serviceType: "Domain ve Hosting Hizmetleri"
      },

      "/hosting": {
        title: "Web Hosting Hizmetleri | Web Center",
        description:
          "Web siteleriniz için hosting çözümleri ve teknik altyapı hizmetleri hakkında Web Center'dan bilgi alın.",
        keywords:
          "hosting, web hosting, Bursa hosting, site hosting, hosting hizmetleri",
        schemaType: "Service",
        serviceName: "Web Hosting",
        serviceType: "Web Hosting Hizmetleri"
      },

      "/domain-ssl": {
        title: "Domain ve SSL Hizmetleri | Web Center",
        description:
          "Domain ve SSL çözümleri ile web sitenizin alan adı ve güvenlik altyapısını oluşturun.",
        keywords:
          "domain, SSL, SSL sertifikası, domain kayıt, web sitesi güvenliği",
        schemaType: "Service",
        serviceName: "Domain ve SSL",
        serviceType: "Domain ve SSL Hizmetleri"
      },

      "/kurumsal-e-posta": {
        title: "Kurumsal E-Posta Çözümleri | Web Center",
        description:
          "İşletmenize özel alan adıyla profesyonel kurumsal e-posta çözümleri hakkında bilgi alın.",
        keywords:
          "kurumsal e-posta, şirket maili, kurumsal mail, domain mail, profesyonel e-posta",
        schemaType: "Service",
        serviceName: "Kurumsal E-Posta",
        serviceType: "Kurumsal E-Posta Hizmetleri"
      },

      "/seo-optimizasyon": {
        title: "SEO Hizmetleri | Web Center Bursa",
        description:
          "Teknik SEO ve web sitesi optimizasyonu ile sitenizin arama motorları tarafından daha iyi anlaşılmasını sağlayın.",
        keywords:
          "SEO, Bursa SEO, SEO hizmetleri, teknik SEO, Google SEO, site optimizasyonu",
        schemaType: "Service",
        serviceName: "SEO Hizmetleri",
        serviceType: "SEO ve Web Sitesi Optimizasyonu"
      },

      "/logo-tasarimi": {
        title: "Logo Tasarımı | Web Center Bursa",
        description:
          "Markanızın kurumsal kimliğine uygun profesyonel logo tasarımı çözümleri.",
        keywords:
          "logo tasarımı, Bursa logo tasarımı, kurumsal logo, marka tasarımı, grafik tasarım",
        schemaType: "Service",
        serviceName: "Logo Tasarımı",
        serviceType: "Logo ve Kurumsal Kimlik Tasarımı"
      },

      "/isletme-profili": {
        title: "Google İşletme Profili Hizmetleri | Web Center",
        description:
          "İşletmenizin Google üzerindeki görünürlüğünü geliştirmeye yönelik Google İşletme Profili çözümleri.",
        keywords:
          "Google İşletme Profili, işletme profili, Google işletme, yerel SEO, Bursa",
        schemaType: "Service",
        serviceName: "Google İşletme Profili",
        serviceType: "Google İşletme Profili Hizmetleri"
      },

      "/google-maps": {
        title: "Google Maps İşletme Çözümleri | Web Center",
        description:
          "İşletmenizin Google Maps üzerindeki bilgilerinin düzenlenmesi ve dijital görünürlüğünün geliştirilmesine yönelik çözümler.",
        keywords:
          "Google Maps, Google Haritalar, işletme konumu, yerel SEO, Google işletme",
        schemaType: "Service",
        serviceName: "Google Maps İşletme Çözümleri",
        serviceType: "Google Maps ve Yerel Görünürlük"
      },

      "/projects": {
        title: "Projelerimiz | Web Center Referansları",
        description:
          "Web Center tarafından geliştirilen web tasarım ve dijital projeleri inceleyin.",
        keywords:
          "Web Center projeleri, web tasarım referansları, Bursa web tasarım projeleri",
        schemaType: "CollectionPage"
      },

      "/faqs": {
        title: "Sıkça Sorulan Sorular | Web Center",
        description:
          "Web tasarım, web tasarım fiyatları ve Web Center hizmetleri hakkında sıkça sorulan soruların yanıtlarını inceleyin.",
        keywords:
          "web tasarım sıkça sorulan sorular, web tasarım fiyatları, Web Center, web tasarım ajansı",
        schemaType: "FAQPage"
      },

      "/contact": {
        title: "İletişim | Web Center Bursa",
        description:
          "Web Center ile iletişime geçin. Web tasarım ve dijital çözümler hakkında bilgi ve teklif alın.",
        keywords:
          "Web Center iletişim, Bursa web tasarım iletişim, web tasarım teklif",
        schemaType: "ContactPage"
      },

      "/about": {
        title: "Hakkımızda | Web Center Bursa",
        description:
          "Web Center'ın web tasarım ve dijital çözümler alanındaki hizmetleri hakkında bilgi alın.",
        keywords:
          "Web Center hakkında, Bursa web tasarım firması, Nilüfer web tasarım",
        schemaType: "AboutPage"
      }
    };

    return (
      seoData[path] || {
        title: defaultTitle,
        description: defaultDescription,
        keywords: defaultKeywords,
        schemaType: "WebPage"
      }
    );
  };

  const pageSEO = getPageSEO();

  const finalTitle = title || pageSEO.title;
  const finalDescription = description || pageSEO.description;
  const finalKeywords = keywords || pageSEO.keywords;

  const cleanPath =
    location.pathname === "/"
      ? "/"
      : location.pathname.replace(/\/+$/, "");

  const finalUrl = url || `${baseUrl}${cleanPath}`;

  // =====================================================
  // WEBPAGE SCHEMA
  // =====================================================

  const webPageTypes = [
    "AboutPage",
    "ContactPage",
    "CollectionPage"
  ];

  const webPageSchema = {
    "@context": "https://schema.org",

    "@type": webPageTypes.includes(pageSEO.schemaType)
      ? pageSEO.schemaType
      : "WebPage",

    "@id": `${finalUrl}#webpage`,

    url: finalUrl,

    name: finalTitle,

    description: finalDescription,

    isPartOf: {
      "@id": "https://webcenter.com.tr/#website"
    },

    about: {
      "@id": "https://webcenter.com.tr/#organization"
    },

    inLanguage: "tr-TR"
  };

  // =====================================================
  // SERVICE SCHEMA
  // =====================================================

  const serviceSchema =
    pageSEO.schemaType === "Service"
      ? {
          "@context": "https://schema.org",

          "@type": "Service",

          "@id": `${finalUrl}#service`,

          name: pageSEO.serviceName,

          serviceType: pageSEO.serviceType,

          description: finalDescription,

          url: finalUrl,

          provider: {
            "@id": "https://webcenter.com.tr/#organization"
          },

          areaServed: [
            {
              "@type": "City",
              name: "Bursa"
            },
            {
              "@type": "Country",
              name: "Türkiye"
            }
          ]
        }
      : null;

  // =====================================================
  // FAQ SCHEMA
  // Sadece /faqs sayfasında çalışır
  // =====================================================

  const faqSchema =
    location.pathname.toLowerCase().replace(/\/+$/, "") === "/faqs"
      ? {
          "@context": "https://schema.org",

          "@type": "FAQPage",

          "@id": "https://webcenter.com.tr/faqs#faq",

          url: "https://webcenter.com.tr/faqs",

          inLanguage: "tr-TR",

          mainEntity: [
            {
              "@type": "Question",

              name: "Kurumsal Web Tasarım NEDİR?",

              acceptedAnswer: {
                "@type": "Answer",

                text:
                  "Kurumsal web tasarım, işletmelerin profesyonel kimliğini yansıtan, modern, kullanıcı dostu ve mobil uyumlu web sitelerinin hazırlanmasıdır. Bu tür tasarımlar, markanızın dijital ortamdaki yüzü olarak güvenilirlik ve profesyonellik sağlar."
              }
            },

            {
              "@type": "Question",

              name:
                "Web Tasarım Ajansı NEDİR ve Neden ÖNEMLİDİR?",

              acceptedAnswer: {
                "@type": "Answer",

                text:
                  "Web tasarım ajansları, kullanıcı deneyimini ön planda tutarak, işletmelerin hedeflerine uygun web siteleri tasarlayan profesyonel firmalardır. Doğru bir ajansla çalışmak, markanızın dijital dünyada fark edilmesini sağlar."
              }
            },

            {
              "@type": "Question",

              name: "Web Tasarım FİYATLARI Ne Kadar?",

              acceptedAnswer: {
                "@type": "Answer",

                text:
                  "Web tasarım fiyatları; sitenin özellikleri, tasarımın karmaşıklığı, sayfa sayısı ve ek hizmetlere (SEO, içerik yönetimi, entegrasyonlar) göre değişiklik gösterir. Web Center, her bütçeye uygun özel çözümler sunar."
              }
            },

            {
              "@type": "Question",

              name:
                "En İYİ Web Tasarım Firması Nasıl SEÇİLİR?",

              acceptedAnswer: {
                "@type": "Answer",

                text:
                  "En iyi web tasarım firmasını seçerken portföy ve referanslar, kullanıcı dostu ve yenilikçi tasarımlar, teknik destek ve garanti hizmetleri, proje teslim süresi ve müşteri memnuniyeti gibi kriterlere dikkat etmelisiniz. Web Center olarak bu kriterlerin tamamını karşılıyoruz."
              }
            },

            {
              "@type": "Question",

              name:
                "Web Tasarım ŞİRKETİ İLE Çalışmanın Avantajları NELERDİR?",

              acceptedAnswer: {
                "@type": "Answer",

                text:
                  "Profesyonel bir web tasarım şirketiyle çalışmak işletmenizin dijital ortamdaki imajını güçlendirir, arama motorlarında üst sıralarda yer almanızı sağlar, mobil uyumluluk ve kullanıcı dostu tasarımlar sunar."
              }
            },

            {
              "@type": "Question",

              name: "Neden Web Center Fİrması?",

              acceptedAnswer: {
                "@type": "Answer",

                text:
                  "Web Center olarak sunduğumuz ayrıcalıklar: Tecrübe ve Güven: Yıllardır sektörde hizmet veren bir firma olarak müşteri memnuniyetine önem veriyoruz. Özelleştirilmiş Çözümler: İşletmenizin ihtiyaçlarına özel web tasarımları hazırlıyoruz. 7/24 Destek: Proje öncesi ve sonrasında tam destek sağlıyoruz. Yenilikçi Yaklaşım: Son teknolojileri kullanarak trendleri yakalayan, modern tasarımlar sunuyoruz. Uygun Fiyatlar: Kaliteli hizmeti erişilebilir fiyatlarla sunuyoruz."
              }
            }
          ]
        }
      : null;

  return (
    <Helmet>
      {/* ===================================================
          TEMEL SEO
          =================================================== */}

      <html lang="tr" />

      <title>{finalTitle}</title>

      <meta
        name="description"
        content={finalDescription}
      />

      <meta
        name="keywords"
        content={finalKeywords}
      />

      <meta
        name="author"
        content={author}
      />

      <meta
        name="robots"
        content="index, follow, max-image-preview:large"
      />

      <meta
        name="googlebot"
        content="index, follow, max-image-preview:large"
      />

      {/* ===================================================
          CANONICAL
          =================================================== */}

      <link
        rel="canonical"
        href={finalUrl}
      />

      {/* ===================================================
          OPEN GRAPH
          =================================================== */}

      <meta
        property="og:type"
        content={type}
      />

      <meta
        property="og:title"
        content={finalTitle}
      />

      <meta
        property="og:description"
        content={finalDescription}
      />

      <meta
        property="og:url"
        content={finalUrl}
      />

      <meta
        property="og:image"
        content={image}
      />

      <meta
        property="og:image:alt"
        content={finalTitle}
      />

      <meta
        property="og:site_name"
        content="Web Center"
      />

      <meta
        property="og:locale"
        content="tr_TR"
      />

      {/* ===================================================
          TWITTER / X
          =================================================== */}

      <meta
        name="twitter:card"
        content="summary_large_image"
      />

      <meta
        name="twitter:title"
        content={finalTitle}
      />

      <meta
        name="twitter:description"
        content={finalDescription}
      />

      <meta
        name="twitter:image"
        content={image}
      />

      {/* ===================================================
          WEBPAGE SCHEMA
          =================================================== */}

      <script type="application/ld+json">
        {JSON.stringify(webPageSchema)}
      </script>

      {/* ===================================================
          SERVICE SCHEMA
          =================================================== */}

      {serviceSchema && (
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      )}

      {/* ===================================================
          FAQ SCHEMA
          =================================================== */}

      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}

    </Helmet>
  );
};

export default SEOHelmet;